export interface QualityResult {
  passed: boolean;
  brightness: number;    // 0-255 average
  blurScore: number;     // higher = sharper
  issues: string[];      // Portuguese messages
}

/**
 * Analyze photo quality from a canvas element.
 * Must be called when the canvas still has the captured image rendered.
 */
export function analyzePhotoQuality(
  canvas: HTMLCanvasElement,
  options?: { minBrightness?: number; maxBrightness?: number; minSharpness?: number }
): QualityResult {
  const ctx = canvas.getContext('2d');
  if (!ctx) return { passed: true, brightness: 128, blurScore: 100, issues: [] };

  const width = canvas.width;
  const height = canvas.height;

  // Sample from center region (60% of image) to avoid watermark area
  const sampleX = Math.floor(width * 0.2);
  const sampleY = Math.floor(height * 0.1);
  const sampleW = Math.floor(width * 0.6);
  const sampleH = Math.floor(height * 0.6);

  const imageData = ctx.getImageData(sampleX, sampleY, sampleW, sampleH);
  const pixels = imageData.data;

  // 1. Brightness check (average luminance)
  let totalBrightness = 0;
  const pixelCount = sampleW * sampleH;
  for (let i = 0; i < pixels.length; i += 4) {
    // ITU-R BT.709 luminance
    totalBrightness += pixels[i] * 0.2126 + pixels[i + 1] * 0.7152 + pixels[i + 2] * 0.0722;
  }
  const brightness = totalBrightness / pixelCount;

  // 2. Blur detection (Laplacian variance approximation)
  // Convert to grayscale, compute discrete Laplacian, measure variance
  const gray = new Float32Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    const idx = i * 4;
    gray[i] = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
  }

  // Laplacian kernel: [0, 1, 0; 1, -4, 1; 0, 1, 0]
  let laplacianSum = 0;
  let laplacianSqSum = 0;
  let laplacianCount = 0;
  for (let y = 1; y < sampleH - 1; y++) {
    for (let x = 1; x < sampleW - 1; x++) {
      const idx = y * sampleW + x;
      const lap = -4 * gray[idx] +
        gray[idx - 1] + gray[idx + 1] +
        gray[idx - sampleW] + gray[idx + sampleW];
      laplacianSum += lap;
      laplacianSqSum += lap * lap;
      laplacianCount++;
    }
  }
  const mean = laplacianSum / laplacianCount;
  const blurScore = (laplacianSqSum / laplacianCount) - (mean * mean); // variance

  // Thresholds (configurable)
  const minBrightness = options?.minBrightness ?? 40;
  const maxBrightness = options?.maxBrightness ?? 220;
  const minSharpness = options?.minSharpness ?? 100;

  const issues: string[] = [];
  if (brightness < minBrightness) {
    issues.push('Foto muito escura. Procure um local com mais iluminacao.');
  }
  if (brightness > maxBrightness) {
    issues.push('Foto muito clara/estourada. Evite luz direta na camera.');
  }
  if (blurScore < minSharpness) {
    issues.push('Foto borrada. Segure o celular firme e tente novamente.');
  }

  return {
    passed: issues.length === 0,
    brightness: Math.round(brightness),
    blurScore: Math.round(blurScore),
    issues,
  };
}
